import { cn } from '@caterinx/ui/lib/utils';
import { Check } from 'lucide-react';

/**
 * Mehrfachauswahl als antippbare Chips statt als Checkbox-Liste.
 *
 * **Der Grund ist der Daumen, nicht die Optik.** Die meisten Gastronomen füllen dieses Profil
 * am Handy aus, oft zwischen zwei Schichten. Eine Checkbox ist 16 Pixel breit; ein Chip ist so
 * groß wie sein Text und erfüllt damit die 44-Pixel-Empfehlung für Berührungsziele ohne
 * Zusatzaufwand. Bei zehn Leistungsarten ist das der Unterschied zwischen „durchgeklickt" und
 * „später am Rechner" — und „später" heißt hier: nie.
 *
 * `aria-pressed` statt `role="checkbox"`: Es sind Schalter, und Screenreader lesen den Zustand
 * dann als „gedrückt" vor, ohne dass eine Gruppensemantik nötig wäre, die es hier nicht gibt.
 */
export function ChipMultiSelect<T extends string>({
  options,
  labels,
  value,
  onChange,
  disabled = false,
}: {
  options: readonly T[];
  labels: Readonly<Record<T, string>>;
  value: readonly T[];
  onChange: (next: T[]) => void;
  disabled?: boolean;
}) {
  const selected = new Set(value);

  const toggle = (option: T): void => {
    const next = new Set(selected);
    if (next.has(option)) {
      next.delete(option);
    } else {
      next.add(option);
    }
    onChange(options.filter((candidate) => next.has(candidate)));
  };

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = selected.has(option);
        return (
          <button
            key={option}
            type="button"
            disabled={disabled}
            aria-pressed={isSelected}
            onClick={() => toggle(option)}
            className={cn(
              'inline-flex min-h-11 items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
              isSelected
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-background hover:bg-accent hover:text-accent-foreground',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {isSelected && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
            {/* eslint-disable-next-line security/detect-object-injection -- `option` stammt aus `options`, also aus dem Zod-Enum */}
            {labels[option]}
          </button>
        );
      })}
    </div>
  );
}
